import { Children, forwardRef, isValidElement, type ButtonHTMLAttributes } from 'react';
import { Icon } from './Icon';

/**
 * `<button mat-button class="action-button primary-button">`: same DOM as Material's MDC button (ripple, leading
 * `<mat-icon>`s projected before the label, label, focus indicator and touch target) and the same classes, so the
 * ported button styles apply and icons align exactly like Angular's `mat-icon` content projection.
 */
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** True while the action is running: the button is disabled so a second click cannot submit twice. */
  busy?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className = '', type = 'button', children, disabled, busy = false, ...rest },
  ref,
) {
  const parts = Children.toArray(children);
  const icons = parts.filter((child) => isValidElement(child) && child.type === Icon);
  const label = parts.filter((child) => !(isValidElement(child) && child.type === Icon));
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={`mdc-button mat-mdc-button mat-unthemed mat-mdc-button-base ${className}`.trim()}
      {...rest}
    >
      <span className="mat-mdc-button-persistent-ripple mdc-button__ripple"></span>
      {icons}
      <span className="mdc-button__label">{label}</span>
      <span className="mat-mdc-focus-indicator"></span>
      <span className="mat-mdc-button-touch-target"></span>
    </button>
  );
});
